import indexedDBService from './indexedDBService';
import {productApi} from '~/api';

/**
 * Product Sync Service
 * Handles syncing products between server and IndexedDB
 */
class ProductSyncService
{
    constructor()
    {
        this.isSyncing = false;
        this.syncCallbacks = [];
        this.hasVerifiedDeleted = false;
    }

    /**
     * Register callback for sync events
     */
    onSyncComplete(callback)
    {
        this.syncCallbacks.push(callback);
    }

    /**
     * Trigger sync complete callbacks
     */
    triggerSyncComplete(data)
    {
        this.syncCallbacks.forEach(callback => callback(data));
    }

    /**
     * Check if sync is needed
     */
    async checkSyncNeeded(serverVersion) {
        try {
            await indexedDBService.init();

            const localVersion = await indexedDBService.getMeta('sync_product_version');

            console.log('Sync version check:', {
                localVersion,
                serverVersion,
                needSync: !localVersion || String(localVersion) !== String(serverVersion)
            });

            if (!localVersion) {
                console.log('First sync - no local version found');
                return true; // First sync
            }

            const needSync = String(localVersion) !== String(serverVersion);

            if (needSync) {
                console.log('Sync needed - versions differ');
            }

            return needSync;
        } catch (error) {
            console.error('Error checking sync:', error);
            return true; // Sync on error
        }
    }

    /**
     * Sync products from server
     */
    async syncProducts(forceFullSync = false)
    {
        if (this.isSyncing)
        {
            console.log('Sync already in progress');
            return { success: false, message: 'Sync already in progress' };
        }

        this.isSyncing = true;

        try {
            console.log('📦 Initializing IndexedDB...');
            await indexedDBService.init();

            // Get last sync time
            let lastSyncTime = null;

            if (!forceFullSync)
            {
                lastSyncTime = await indexedDBService.getMeta('sync_product_time');
            }

            console.log('🔄 Starting product sync...', { lastSyncTime, forceFullSync });

            // Call API to get delta changes
            console.log('📡 Calling API...');
            const response = await productApi.sync({
                last_sync_time: lastSyncTime
            });

            console.log('📡 API Response:', response);

            if (response.status !== 'success')
            {
                throw new Error(response.message || 'Sync failed');
            }

            const { products, sync_product_version, sync_time } = response.data;

            console.log(`📊 Received ${products?.length || 0} products from server`);
            console.log('Version:', sync_product_version, 'Time:', sync_time);

            // Process products
            let added = 0;
            let updated = 0;
            let deleted = 0;

            for (const product of products)
            {
                if (product.deleted_at || Number(product.trash) === 1) {
                    // Delete product (đã xóa hẳn hoặc nằm trong thùng rác)
                    await indexedDBService.deleteProduct(product.id);
                    deleted++;
                    console.log(`🗑️ Deleted product ID: ${product.id}`);
                }
                else
                {
                    // Add or update product
                    const existing = await indexedDBService.getProduct(product.id);

                    await indexedDBService.saveProduct(product);

                    if (existing)
                    {
                        updated++;
                        console.log(`✏️ Updated product ID: ${product.id} - ${product.title}`);
                    }
                    else
                    {
                        added++;
                        console.log(`➕ Added product ID: ${product.id} - ${product.title}`);
                    }
                }
            }

            // Update meta
            console.log('💾 Updating meta data...');
            await indexedDBService.setMeta('sync_product_version', sync_product_version);

            await indexedDBService.setMeta('sync_product_time', sync_time);

            const count = await indexedDBService.getProductCount();

            const result = {
                success: true,
                added,
                updated,
                deleted,
                total: count,
                version: sync_product_version,
                syncTime: sync_time
            };

            console.log('Sync completed:', result);

            this.triggerSyncComplete(result);

            return result;
        }
        catch (error)
        {
            console.error('Sync error:', error);

            return {
                success: false,
                message: error.message || 'Sync failed'
            };
        }
        finally
        {
            this.isSyncing = false;
        }
    }

    /**
     * Verify local products against server
     * Gửi toàn bộ ID sản phẩm trong IndexedDB lên server,
     * server trả về những ID đã bị xóa (thùng rác hoặc không còn tồn tại) để xóa khỏi local.
     * Chỉ chạy 1 lần mỗi lần mở app.
     */
    async verifyDeletedProducts()
    {
        if (this.hasVerifiedDeleted)
        {
            return { success: false, message: 'Already verified' };
        }

        this.hasVerifiedDeleted = true;

        try {
            await indexedDBService.init();

            const products = await indexedDBService.getAllProducts();

            if (!products.length)
            {
                return { success: true, deleted: 0 };
            }

            // Map String(id) -> key thực tế trong IndexedDB (key phân biệt kiểu số/chuỗi)
            const localKeyMap = new Map(products.map(product => [String(product.id), product.id]));

            const ids = [...localKeyMap.values()];

            console.log(`🔍 Verifying ${ids.length} local products with server...`);

            let deleted = 0;

            const chunkSize = 500;

            for (let i = 0; i < ids.length; i += chunkSize)
            {
                const chunk = ids.slice(i, i + chunkSize);

                const response = await productApi.syncVerify(chunk);

                if (response.status !== 'success')
                {
                    throw new Error(response.message || 'Verify failed');
                }

                const deletedIds = response.data?.deleted_ids || [];

                for (const id of deletedIds)
                {
                    const localKey = localKeyMap.get(String(id));

                    if (localKey !== undefined)
                    {
                        await indexedDBService.deleteProduct(localKey);
                        deleted++;
                        console.log(`🗑️ Verify: deleted product ID ${id} (removed on server)`);
                    }
                }
            }

            console.log(`✅ Verify completed: ${deleted} stale products removed`);

            return { success: true, deleted };
        }
        catch (error)
        {
            console.error('Verify deleted products error:', error);

            // Cho phép chạy lại ở lần sync sau nếu request lỗi (vd: mất mạng)
            this.hasVerifiedDeleted = false;

            return {
                success: false,
                message: error.message || 'Verify failed'
            };
        }
    }

    /**
     * Force full sync (clear and re-sync all)
     */
    async forceFullSync()
    {
        try
        {
            await indexedDBService.init();
            await indexedDBService.clearProducts();
            await indexedDBService.setMeta('sync_product_time', null);

            return await this.syncProducts(true);
        }
        catch (error)
        {
            console.error('Force full sync error:', error);
            return {
                success: false,
                message: error.message || 'Force sync failed'
            };
        }
    }

    /**
     * Search products locally
     */
    async searchProducts(keyword, limit = 50) {
        try {
            await indexedDBService.init();
            return await indexedDBService.searchProducts(keyword, limit);
        } catch (error) {
            console.error('Search error:', error);
            return [];
        }
    }

    /**
     * Get all products
     */
    async getAllProducts() {
        try {
            await indexedDBService.init();
            return await indexedDBService.getAllProducts();
        } catch (error) {
            console.error('Get all products error:', error);
            return [];
        }
    }

    /**
     * Get product by ID
     */
    async getProduct(id) {
        try {
            await indexedDBService.init();
            return await indexedDBService.getProduct(id);
        } catch (error) {
            console.error('Get product error:', error);
            return null;
        }
    }

    /**
     * Get sync status
     */
    async getSyncStatus() {
        try {
            await indexedDBService.init();

            const version = await indexedDBService.getMeta('sync_product_version');
            const syncTime = await indexedDBService.getMeta('sync_product_time');
            const count = await indexedDBService.getProductCount();

            return {
                version,
                syncTime,
                count,
                isSyncing: this.isSyncing
            };
        } catch (error) {
            console.error('Get sync status error:', error);
            return null;
        }
    }
}

// Export singleton instance
const productSyncService = new ProductSyncService();

export default productSyncService;
